import { useState, useCallback, useRef, useEffect } from 'react';
import { useSessions } from '../hooks/useSessions';
import Sidebar from './Sidebar';
import ContentGapMain from './ContentGapMain';
import VerifyEmailBanner from './VerifyEmailBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const C = {
  bg: '#0F0F0F',
  surf: '#161616',
  card: '#1C1C1C',
  bdr: '#2A2A2A',
  accent: '#D97757',
  accentS: 'rgba(217,119,87,0.10)',
  accentB: 'rgba(217,119,87,0.28)',
  green: '#4CAF82',
  greenS: 'rgba(76,175,130,0.12)',
  greenB: 'rgba(76,175,130,0.3)',
  orange: '#F0A050',
  text: '#F0EDE8',
  muted: '#888',
  dim: '#2A2A2A',
  sidebar: '#111111',
};

const G = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: ${C.bg}; color: ${C.text}; font-family: 'Inter', sans-serif; -webkit-font-smoothing: antialiased; overflow: hidden; }
  @keyframes fadeUp  { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:translateY(0) } }
  @keyframes slideIn { from { opacity:0; transform:translateX(-8px) } to { opacity:1; transform:translateX(0) } }
  @keyframes spin    { to { transform: rotate(360deg) } }
  @keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:0.25} }
  @keyframes slideUp { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-thumb { background: #2A2A2A; border-radius: 4px; }
  input, textarea { color-scheme: dark; font-family: 'Inter', sans-serif; }
`;

function Spinner({ color = C.accent, size = 22 }) {
  return <div style={{ width: size, height: size, border: `1.5px solid ${C.bdr}`, borderTopColor: color, borderRadius: '50%', animation: 'spin .7s linear infinite', flexShrink: 0 }} />;
}

function SyncBadge({ status }) {
  if (status === 'idle') return null;
  const map = {
    saving: { label: 'Saving...', color: C.orange },
    saved: { label: '✓ Saved', color: C.green },
    error: { label: '✗ Sync error', color: '#FF7070' },
  };
  const s = map[status];
  return (
    <span style={{ fontSize: 12, color: s.color, fontFamily: 'JetBrains Mono, monospace', background: C.surf, border: `0.5px solid ${C.bdr}`, padding: '2px 8px', borderRadius: 4 }}>
      {s.label}
    </span>
  );
}

export default function ContentGapApp({ user }) {
  const {
    sessions,
    activeId,
    activeSession,
    setActiveId,
    addSession,
    removeSession,
    updateSession,
    loading: sessionsLoading,
    syncStatus,
  } = useSessions();

  const [sideCollapsed, setSideCollapsed] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [toast, setToast] = useState('');

  const showToast = (m) => {
    setToast(m);
    setTimeout(() => setToast(''), 3000);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = '/login';
    }
  };

  if (sessionsLoading) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
        <Spinner size={32} />
        <span style={{ fontSize: 14, color: C.muted, fontFamily: 'JetBrains Mono, monospace' }}>Loading your sessions...</span>
      </div>
    );
  }

  return (
    <>
      <style>{G}</style>
      <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        <Sidebar
          sessions={sessions}
          activeId={activeId}
          analyzing={analyzing}
          user={user}
          onSelect={setActiveId}
          onAdd={addSession}
          onRemove={removeSession}
          onLogout={handleLogout}
          collapsed={sideCollapsed}
          onToggle={() => setSideCollapsed((p) => !p)}
          showToast={showToast}
        />

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          <VerifyEmailBanner user={user} />
          {/* Top bar - session tabs only (ContentGap brand moved to sidebar) */}
          <div style={{ height: 54, padding: '0 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: `1px solid ${C.bdr}`, background: 'rgba(15,15,15,0.97)', flexShrink: 0 }}>
            {/* Session tabs */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4, overflowX: 'auto', padding: '0 4px' }}>
              {sessions.map((s, idx) => (
                <div
                  key={s.id}
                  onClick={() => setActiveId(s.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '5px 10px',
                    borderRadius: 7,
                    cursor: 'pointer',
                    background: s.id === activeId ? C.accentS : 'transparent',
                    border: `0.5px solid ${s.id === activeId ? C.accentB : C.bdr}`,
                    whiteSpace: 'nowrap',
                    fontSize: 13,
                    fontWeight: s.id === activeId ? 500 : 400,
                    color: s.id === activeId ? C.text : C.muted,
                    flexShrink: 0,
                    transition: 'all .15s',
                  }}
                >
                  <span style={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: s.id === activeId ? C.accent : C.dim }}>#{idx + 1}</span>
                  <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.url || 'New session'}</span>
                  {analyzing === s.id && <Spinner size={10} color={C.orange} />}
                  {s.done.includes(5) && !analyzing && <span style={{ color: C.green, fontSize: 12 }}>✓</span>}
                  {s.topics && s.topics.length > 0 && !s.done.includes(5) && (
                    <span style={{ fontSize: 11, color: C.muted, fontFamily: 'JetBrains Mono, monospace' }}>{s.topics.length}g</span>
                  )}
                </div>
              ))}
              <button
                onClick={() => addSession('', '')}
                style={{
                  padding: '5px 9px',
                  background: 'transparent',
                  border: `0.5px dashed ${C.bdr}`,
                  borderRadius: 7,
                  color: C.muted,
                  cursor: 'pointer',
                  fontSize: 13,
                  flexShrink: 0,
                }}
              >
                + New
              </button>
            </div>

            {/* Sync status */}
            <SyncBadge status={syncStatus} />
          </div>

          <div style={{ flex: 1, overflow: 'hidden' }}>
            {activeSession && (
              <ContentGapMain
                key={activeId}
                session={activeSession}
                updateSession={updateSession}
                setAnalyzing={setAnalyzing}
                showToast={showToast}
              />
            )}
          </div>
        </div>

        {toast && (
          <div
            style={{
              position: 'fixed',
              bottom: 22,
              right: 22,
              background: C.card,
              border: `0.5px solid ${C.bdr}`,
              color: C.text,
              padding: '9px 16px',
              borderRadius: 8,
              fontSize: 14.5,
              fontWeight: 500,
              zIndex: 1000,
              animation: 'slideUp .25s ease',
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            }}
          >
            <span style={{ color: C.green }}>✓</span> {toast}
          </div>
        )}
      </div>
    </>
  );
}
